import React from 'react';
import { Document, Page, Text, View, StyleSheet } from '@react-pdf/renderer';

const styles = StyleSheet.create({
  page: {
    paddingTop: 44,
    paddingBottom: 70,
    paddingHorizontal: 44,
    fontFamily: 'Helvetica',
    fontSize: 10,
    color: '#222',
  },
  header: { fontSize: 22, fontWeight: 'bold', marginBottom: 8 },
  subheader: { fontSize: 14, fontWeight: 'bold', marginBottom: 4, marginTop: 16 },
  text: { fontSize: 10, marginBottom: 4 },
  small: { fontSize: 9, color: '#555' },
  divider: { borderBottomWidth: 1, borderBottomColor: '#ddd', marginVertical: 10 },
  row: { flexDirection: 'row', justifyContent: 'space-between', marginBottom: 3 },
  tableHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    backgroundColor: '#f5f5f5',
    padding: 6,
    marginBottom: 2,
    fontWeight: 'bold',
  },
  tableRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    padding: 6,
    borderBottomWidth: 1,
    borderBottomColor: '#eee',
  },
  col70: { width: '70%' },
  col30: { width: '30%', textAlign: 'right' },
  totalRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    padding: 6,
    marginTop: 4,
    fontWeight: 'bold',
  },
  footer: {
    position: 'absolute',
    bottom: 30,
    left: 44,
    right: 44,
    fontSize: 8,
    color: '#999',
    textAlign: 'center',
  },
  pageNumber: {
    position: 'absolute',
    fontSize: 9,
    top: 20,
    right: 44,
    color: '#999',
  },
});

/**
 * TransactionInvoice — PDF document for a transaction receipt.
 *
 * @param {Object} props
 * @param {Object} props.transaction — Sharetribe transaction object
 * @param {string} props.marketplaceName — e.g. "My Marketplace"
 * @param {string} [props.marketplaceUrl]
 */
const TransactionInvoice = ({ transaction, marketplaceName, marketplaceUrl }) => {
  if (!transaction) return null;

  const { attributes } = transaction;
  const txId = transaction.id?.uuid || 'N/A';
  const listingTitle = transaction.listing?.attributes?.title || 'Listing';
  const customerName = transaction.customer?.attributes?.profile?.displayName || 'Customer';
  const providerName = transaction.provider?.attributes?.profile?.displayName || 'Provider';
  const lineItems = attributes?.lineItems || [];
  const payinTotal = attributes?.payinTotal;
  const createdAt = attributes?.createdAt;
  const date = createdAt ? new Date(createdAt).toLocaleDateString() : '-';

  const formatMoney = money => {
    if (!money) return '-';
    const amount = (money.amount / 100).toFixed(2);
    return `${amount} ${money.currency}`;
  };

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        <Text style={styles.pageNumber} render={({ pageNumber, totalPages }) =>
          `${pageNumber} / ${totalPages}`
        } fixed />

        {/* Header */}
        <Text style={styles.header}>Receipt</Text>
        <Text style={styles.text}>Date: {date}</Text>
        <Text style={styles.text}>Transaction ID: {txId}</Text>
        <Text style={styles.text}>Marketplace: {marketplaceName || '-'}</Text>
        {marketplaceUrl && <Text style={styles.small}>{marketplaceUrl}</Text>}

        <View style={styles.divider} />

        {/* Parties */}
        <View style={styles.row}>
          <View style={{ width: '48%' }}>
            <Text style={styles.subheader}>Seller</Text>
            <Text style={styles.text}>{providerName}</Text>
          </View>
          <View style={{ width: '48%' }}>
            <Text style={styles.subheader}>Buyer</Text>
            <Text style={styles.text}>{customerName}</Text>
          </View>
        </View>

        <View style={styles.divider} />

        {/* Line items */}
        <Text style={styles.subheader}>Item</Text>
        <View style={styles.tableHeader}>
          <Text style={styles.col70}>Description</Text>
          <Text style={styles.col30}>Amount</Text>
        </View>
        <View style={styles.tableRow}>
          <Text style={styles.col70}>{listingTitle}</Text>
          <Text style={styles.col30}>{formatMoney(payinTotal)}</Text>
        </View>

        {lineItems.map((item, i) =>
          item.code && item.code !== 'line-item/day' && item.code !== 'line-item/item' ? (
            <View key={i} style={styles.tableRow}>
              <Text style={styles.col70}>{item.code.replace('line-item/', '')}</Text>
              <Text style={styles.col30}>{formatMoney(item.lineTotal)}</Text>
            </View>
          ) : null
        )}

        <View style={styles.divider} />

        <View style={styles.totalRow}>
          <Text style={styles.col70}>Total</Text>
          <Text style={styles.col30}>{formatMoney(payinTotal)}</Text>
        </View>

        {/* Footer */}
        <Text style={styles.footer} fixed>
          {`${marketplaceName || 'Marketplace'} — Generated ${new Date().toLocaleDateString()}`}
        </Text>
      </Page>
    </Document>
  );
};

export default TransactionInvoice;
